import React, { useEffect, useState } from "react";
import { FileText, Paperclip, X, Image as ImageIcon } from "lucide-react";

/**
 * AttachmentThumb — renderiza los adjuntos (imagen/PDF) asociados a un registro
 * (bill, expense, payment, etc). Muestra miniaturas clicables + un botón
 * "Ver adjunto" y un modal ligero para previsualizar imágenes a tamaño completo.
 *
 * Props:
 *  - api / token: contexto de autenticación (para bajar el blob)
 *  - ids: string[] — id de FileRecord adjuntos
 *  - testid: prefijo para data-testids
 *  - label: texto del botón "Ver adjunto" (default)
 */
export default function AttachmentThumb({ api, token, ids = [], testid, label = "Ver adjunto" }) {
  const [recs, setRecs] = useState([]);
  const [blobs, setBlobs] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let alive = true;
    const cleanup = { urls: [] };
    if (!ids || !ids.length) { setRecs([]); setBlobs({}); return; }
    (async () => {
      try {
        const { data } = await api.get("/files");
        if (!alive) return;
        const mine = (data || []).filter((r) => ids.includes(r.id));
        setRecs(mine);
        const map = {};
        for (const r of mine) {
          try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.storage_path}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!resp.ok) continue;
            const b = await resp.blob();
            const u = URL.createObjectURL(b);
            cleanup.urls.push(u);
            map[r.id] = u;
          } catch (err) { console.warn("AttachmentThumb: blob fetch failed", r.id, err); }
        }
        if (alive) setBlobs(map);
      } catch (err) { console.warn("AttachmentThumb: /files list failed", err); }
    })();
    return () => { alive = false; cleanup.urls.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join("|")]);

  if (!ids || !ids.length) return <span className="text-slate-300 text-[11px]">—</span>;
  const isPdf = (r) => (r.content_type || "").includes("pdf") || (r.original_filename || "").toLowerCase().endsWith(".pdf");
  const isImage = (r) => (r.content_type || "").startsWith("image/") || /\.(png|jpe?g|webp|gif|heic|heif|bmp)$/i.test(r.original_filename || "");

  return (
    <>
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
        {recs.map((r) => {
          const url = blobs[r.id];
          if (isPdf(r)) {
            return (
              <a
                key={r.id}
                href={url || "#"}
                target="_blank"
                rel="noreferrer"
                download={r.original_filename}
                data-testid={`${testid}-pdf-${r.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                title={r.original_filename}
              >
                <FileText className="w-3.5 h-3.5" /> PDF
              </a>
            );
          }
          if (!isImage(r)) {
            // DOC / XLS / CSV / TXT and other non-image, non-PDF attachments.
            const ext = (r.original_filename || "").split(".").pop()?.toUpperCase().slice(0, 4) || "DOC";
            return (
              <a
                key={r.id}
                href={url || "#"}
                target="_blank"
                rel="noreferrer"
                download={r.original_filename}
                data-testid={`${testid}-file-${r.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100"
                title={r.original_filename}
              >
                <FileText className="w-3.5 h-3.5" /> {ext}
              </a>
            );
          }
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => setPreview({ rec: r, url })}
              data-testid={`${testid}-img-${r.id}`}
              className="w-10 h-10 rounded border border-slate-200 overflow-hidden hover:ring-2 hover:ring-indigo-400 relative bg-slate-100"
              title={r.original_filename}
            >
              {url ? (
                <img src={url} alt={r.original_filename} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-4 h-4 text-slate-400 m-auto mt-3" />
              )}
            </button>
          );
        })}
        {ids.length > recs.length && (
          <span className="text-[10px] text-slate-500 font-mono">+{ids.length - recs.length}</span>
        )}
        {recs.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const first = recs.find((r) => !isPdf(r)) || recs[0];
              if (first && blobs[first.id]) window.open(blobs[first.id], "_blank");
            }}
            data-testid={`${testid}-open`}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 ms-1"
          >
            <Paperclip className="w-3 h-3" /> {label}
          </button>
        )}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid={`${testid}-modal`}
        >
          <div
            className="bg-white rounded-xl overflow-hidden max-w-4xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between gap-4">
              <div className="text-xs font-semibold text-slate-700 truncate">{preview.rec.original_filename}</div>
              <div className="flex items-center gap-2">
                <a
                  href={preview.url}
                  download={preview.rec.original_filename}
                  className="text-[11px] font-semibold px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Descargar
                </a>
                <button
                  onClick={() => setPreview(null)}
                  className="p-1 rounded hover:bg-slate-100"
                  aria-label="cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="bg-slate-900 flex items-center justify-center overflow-auto">
              <img
                src={preview.url}
                alt={preview.rec.original_filename}
                className="max-h-[80vh] max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
